import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, TrashSimple, ArrowUp, ArrowDown, ArrowLeft, FloppyDisk } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { engApi, FIELD_TYPE_LABEL, FORM_STATUS_LABEL } from './engApi';

const newField = () => ({ id: crypto.randomUUID(), type: 'text', label: '', required: false, options: [], placeholder: '' });
const OPTION_TYPES = ['select', 'radio', 'checkbox'];

function FieldEditor({ field, index, total, onChange, onRemove, onMove }) {
  const set = (k, v) => onChange({ ...field, [k]: v });
  return (
    <div className="bg-white rounded-xl border border-zinc-200 p-4 space-y-3" data-testid={`eng-fb-field-${index}`}>
      <div className="flex items-center gap-2">
        <span className="text-xs font-bold text-zinc-400 shrink-0">Q{index + 1}</span>
        <select value={field.type} onChange={(e) => set('type', e.target.value)}
          className="h-9 rounded-md border border-zinc-200 bg-white px-2 text-xs outline-none focus:border-teal-400"
          data-testid={`eng-fb-type-${index}`}>
          {Object.entries(FIELD_TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <div className="ml-auto flex items-center gap-1">
          <button onClick={() => onMove(-1)} disabled={index === 0} className="p-1.5 rounded hover:bg-zinc-100 disabled:opacity-30" data-testid={`eng-fb-up-${index}`}>
            <ArrowUp size={14} />
          </button>
          <button onClick={() => onMove(1)} disabled={index === total - 1} className="p-1.5 rounded hover:bg-zinc-100 disabled:opacity-30" data-testid={`eng-fb-down-${index}`}>
            <ArrowDown size={14} />
          </button>
          <button onClick={onRemove} className="p-1.5 rounded text-rose-500 hover:bg-rose-50" data-testid={`eng-fb-remove-${index}`}>
            <TrashSimple size={14} />
          </button>
        </div>
      </div>
      <Input value={field.label} onChange={(e) => set('label', e.target.value)}
        placeholder="質問・項目名（例: 加工温度 / 不良箇所の写真）" className="h-10" data-testid={`eng-fb-label-${index}`} />
      {OPTION_TYPES.includes(field.type) && (
        <div>
          <Label className="text-[11px] font-semibold text-zinc-500">選択肢（1行に1つ）</Label>
          <Textarea value={(field.options || []).join('\n')}
            onChange={(e) => set('options', e.target.value.split('\n'))}
            rows={3} placeholder={'OK\nNG\n要確認'} className="mt-1 text-sm" data-testid={`eng-fb-options-${index}`} />
        </div>
      )}
      <div className="flex items-center justify-between">
        <span className="text-xs text-zinc-500">必須</span>
        <Switch checked={!!field.required} onCheckedChange={(v) => set('required', v)} data-testid={`eng-fb-required-${index}`} />
      </div>
    </div>
  );
}

export default function EngFormBuilder() {
  const navigate = useNavigate();
  const { fid } = useParams();
  const editing = !!fid;
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('open');
  const [fields, setFields] = useState([newField()]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(editing);

  useEffect(() => {
    if (!editing) return;
    engApi.form(fid).then((f) => {
      setTitle(f.title || ''); setDescription(f.description || '');
      setStatus(f.status || 'draft'); setFields(f.fields?.length ? f.fields : [newField()]);
    }).catch(() => toast.error('フォームの取得に失敗しました')).finally(() => setLoading(false));
  }, [editing, fid]);

  const updateField = (i, f) => setFields((arr) => arr.map((x, idx) => (idx === i ? f : x)));
  const removeField = (i) => setFields((arr) => arr.filter((_, idx) => idx !== i));
  const moveField = (i, dir) => setFields((arr) => {
    const j = i + dir;
    if (j < 0 || j >= arr.length) return arr;
    const copy = [...arr];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    return copy;
  });

  const save = async () => {
    if (!title.trim()) { toast.error('タイトルは必須です'); return; }
    const cleaned = fields
      .map((f) => ({ ...f, label: f.label.trim(), options: (f.options || []).map((o) => o.trim()).filter(Boolean) }))
      .filter((f) => f.label);
    if (cleaned.length === 0) { toast.error('項目を1つ以上追加してください'); return; }
    setSaving(true);
    try {
      const body = { title: title.trim(), description: description.trim() || null, status, fields: cleaned };
      if (editing) await engApi.updateForm(fid, body);
      else await engApi.createForm(body);
      toast.success('保存しました');
      navigate('/engineering/forms');
    } catch (e) {
      toast.error(e?.response?.data?.detail || '保存に失敗しました');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="py-16 text-center text-sm text-zinc-400">読込中…</div>;

  return (
    <div className="space-y-5 max-w-2xl" data-testid="eng-form-builder">
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={() => navigate('/engineering/forms')} className="p-2 rounded-lg hover:bg-zinc-100" data-testid="eng-fb-back">
          <ArrowLeft size={18} />
        </button>
        <h1 className="text-xl font-semibold tracking-tight text-zinc-900">{editing ? 'フォームを編集' : 'フォームを作成'}</h1>
        <div className="ml-auto flex items-center gap-2">
          <select value={status} onChange={(e) => setStatus(e.target.value)}
            className="h-10 rounded-md border border-zinc-200 bg-white px-3 text-sm outline-none focus:border-teal-400"
            data-testid="eng-fb-status">
            {Object.entries(FORM_STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <Button onClick={save} disabled={saving} className="bg-teal-700 hover:bg-teal-800" data-testid="eng-fb-save">
            <FloppyDisk size={16} className="mr-1" /> {saving ? '保存中…' : '保存'}
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-zinc-200 p-4 space-y-3">
        <div>
          <Label className="text-xs font-semibold text-zinc-600">タイトル ＊</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder="例: プレス加工 温度サンプリング" className="h-10 mt-1" data-testid="eng-fb-title" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-zinc-600">説明</Label>
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2}
            placeholder="回答者向けの説明・注意事項" className="mt-1" data-testid="eng-fb-desc" />
        </div>
      </div>

      <div className="space-y-3">
        {fields.map((f, i) => (
          <FieldEditor key={f.id} field={f} index={i} total={fields.length}
            onChange={(nf) => updateField(i, nf)} onRemove={() => removeField(i)} onMove={(dir) => moveField(i, dir)} />
        ))}
      </div>

      <Button variant="outline" onClick={() => setFields((arr) => [...arr, newField()])}
        className="w-full border-dashed border-teal-300 text-teal-700 hover:bg-teal-50" data-testid="eng-fb-add-field">
        <Plus size={16} className="mr-1" /> 項目を追加
      </Button>
    </div>
  );
}
